(function () {
  const controllerName = 'lovenoteReviewTracking';

  if (window[controllerName]) {
    window[controllerName].initAll();
    return;
  }

  const initializedSections = new WeakSet();
  const seenReviews = new WeakSet();
  const seenTimers = new WeakMap();
  const seenThreshold = 0.6;
  const seenDwellMs = 2000;

  function publish(eventName, payload) {
    const analytics = window.Shopify && window.Shopify.analytics;

    if (!analytics || typeof analytics.publish !== 'function') return;

    analytics.publish(`lovenote_reviews:${eventName}`, {
      source: 'customer_reviews',
      ...payload,
    });
  }

  function getReviewPayload(section, review, extra) {
    return {
      section_id: section.dataset.reviewSectionId || '',
      slider_id: section.querySelector('.instagram-reviews__grid')?.id || '',
      review_id: review.dataset.reviewId || review.id || '',
      author: review.dataset.reviewAuthor || '',
      position: Number(review.dataset.reviewPosition || 0),
      has_full_review: review.dataset.reviewHasFull === 'true',
      ...extra,
    };
  }

  function getCurrentReview(section) {
    const slider = section.querySelector('.instagram-reviews__grid');
    const reviews = Array.from(section.querySelectorAll('.instagram-reviews__item')).filter(
      (review) => review.clientWidth > 0
    );

    if (!slider || reviews.length === 0) return null;

    return reviews.reduce((closestReview, review) => {
      const closestDistance = Math.abs(closestReview.offsetLeft - slider.scrollLeft);
      const reviewDistance = Math.abs(review.offsetLeft - slider.scrollLeft);

      return reviewDistance < closestDistance ? review : closestReview;
    }, reviews[0]);
  }

  function isReviewVisible(section, review) {
    const slider = section.querySelector('.instagram-reviews__grid');

    if (!slider) return review.getBoundingClientRect().width > 0;

    const sliderRect = slider.getBoundingClientRect();
    const reviewRect = review.getBoundingClientRect();

    if (reviewRect.width === 0) return false;

    const visibleWidth = Math.min(reviewRect.right, sliderRect.right) - Math.max(reviewRect.left, sliderRect.left);

    return visibleWidth / reviewRect.width >= seenThreshold;
  }

  function markReviewSeen(section, review) {
    if (seenReviews.has(review)) return;

    seenReviews.add(review);
    seenTimers.delete(review);
    publish(
      'review_seen',
      getReviewPayload(section, review, {
        visibility_threshold: seenThreshold,
        dwell_ms: seenDwellMs,
      })
    );
  }

  function scheduleSeen(section, review) {
    if (seenReviews.has(review) || seenTimers.has(review)) return;

    const timer = window.setTimeout(() => markReviewSeen(section, review), seenDwellMs);
    seenTimers.set(review, timer);
  }

  function cancelSeen(review) {
    const timer = seenTimers.get(review);

    if (!timer) return;

    window.clearTimeout(timer);
    seenTimers.delete(review);
  }

  function initSeenTracking(section, reviews) {
    if (!('IntersectionObserver' in window)) {
      reviews.forEach((review) => {
        if (isReviewVisible(section, review)) {
          scheduleSeen(section, review);
        }
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const review = entry.target;

          if (seenReviews.has(review)) return;

          if (entry.isIntersecting && entry.intersectionRatio >= seenThreshold) {
            scheduleSeen(section, review);
          } else {
            cancelSeen(review);
          }
        });
      },
      { threshold: [0, seenThreshold] }
    );

    reviews.forEach((review) => observer.observe(review));
  }

  function initDetailsTracking(section) {
    section.querySelectorAll('.instagram-reviews__details').forEach((details) => {
      details.addEventListener('toggle', () => {
        const review = details.closest('.instagram-reviews__item');

        if (!review) return;

        publish(details.open ? 'review_expanded' : 'review_collapsed', getReviewPayload(section, review));
      });
    });
  }

  function initNavigationTracking(section) {
    const sliderComponent = section.querySelector('.instagram-reviews__slider');

    section.querySelectorAll('.instagram-reviews__button').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;

        const currentReview = getCurrentReview(section);

        publish('review_navigation', {
          section_id: section.dataset.reviewSectionId || '',
          direction: button.name === 'next' ? 'next' : 'previous',
          current_review_id: currentReview?.dataset.reviewId || '',
          current_author: currentReview?.dataset.reviewAuthor || '',
          current_position: Number(currentReview?.dataset.reviewPosition || 0),
        });
      });
    });

    sliderComponent?.addEventListener('slideChanged', (event) => {
      const review = event.detail?.currentElement;

      if (!review || !review.classList.contains('instagram-reviews__item')) return;

      publish(
        'review_slide_changed',
        getReviewPayload(section, review, {
          current_page: Number(event.detail?.currentPage || 0),
        })
      );
    });
  }

  function initSection(section) {
    if (initializedSections.has(section)) return;

    const reviews = Array.from(section.querySelectorAll('.instagram-reviews__item'));

    if (reviews.length === 0) return;

    initializedSections.add(section);
    initSeenTracking(section, reviews);
    initDetailsTracking(section);
    initNavigationTracking(section);
  }

  function initAll(root) {
    const scope = root || document;
    const sections = scope.matches?.('[data-lovenote-review-section]')
      ? [scope]
      : Array.from(scope.querySelectorAll('[data-lovenote-review-section]'));

    sections.forEach(initSection);
  }

  window[controllerName] = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
